import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { ScrollArea } from './ui/scroll-area';
import { 
  Car, 
  Plus, 
  Pencil, 
  Trash2, 
  Save,
  X,
  Loader2,
  Truck,
  Caravan,
  Mountain,
  Ruler,
  Weight,
  AlertTriangle,
  Check,
  Settings2,
  List,
  Search,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const vehicleTypes = [
  { value: 'oldtimer', label: 'Oldtimer / Klassiker', icon: Car, description: 'Klassische Fahrzeuge mit empfindlicher Mechanik' },
  { value: 'caravan', label: 'Wohnwagen', icon: Caravan, description: 'Anhänger mit begrenzter Manövrierfähigkeit' },
  { value: 'motorhome', label: 'Wohnmobil', icon: Truck, description: 'Große Fahrzeuge mit Höhenbeschränkungen' },
];

const presetProfiles = {
  oldtimer: { max_gradient: 12, height: null, width: null, length: null, weight: null, avoid_narrow_roads: true },
  caravan: { max_gradient: 10, height: 2.8, width: 2.3, length: 8, weight: 2500, avoid_narrow_roads: true },
  motorhome: { max_gradient: 15, height: 3.2, width: 2.4, length: 7, weight: 3500, avoid_narrow_roads: true },
};

// International Classic Car Database
const classicCars = [
  // German Classics
  { name: 'VW Käfer (Beetle)', country: '🇩🇪', year: '1938-2003', max_gradient: 10, weight: 840, description: 'Der meistverkaufte Oldtimer der Welt' },
  { name: 'Mercedes-Benz W123', country: '🇩🇪', year: '1976-1986', max_gradient: 15, weight: 1400, description: 'Zuverlässiger Klassiker' },
  { name: 'Mercedes-Benz W114/W115', country: '🇩🇪', year: '1968-1976', max_gradient: 14, weight: 1350, description: 'Strich-Acht' },
  { name: 'Mercedes-Benz 190 SL', country: '🇩🇪', year: '1955-1963', max_gradient: 12, weight: 1160, description: 'Eleganter Roadster' },
  { name: 'Porsche 911 (Classic)', country: '🇩🇪', year: '1964-1989', max_gradient: 18, weight: 1080, description: 'Sportwagen-Ikone' },
  { name: 'Porsche 356', country: '🇩🇪', year: '1948-1965', max_gradient: 15, weight: 830, description: 'Erster Porsche Sportwagen' },
  { name: 'BMW 02 (2002)', country: '🇩🇪', year: '1966-1977', max_gradient: 16, weight: 1020, description: 'Kompakter Sportler' },
  { name: 'BMW E30 (3er)', country: '🇩🇪', year: '1982-1994', max_gradient: 18, weight: 1100, description: 'Youngtimer-Klassiker' },
  { name: 'Opel Kadett C', country: '🇩🇪', year: '1973-1979', max_gradient: 14, weight: 850, description: 'Beliebter Kompaktwagen' },
  { name: 'VW Bus T1 (Bulli)', country: '🇩🇪', year: '1950-1967', max_gradient: 8, weight: 1200, height: 1.94, description: 'Kultiger Transporter' },
  { name: 'VW Bus T2', country: '🇩🇪', year: '1967-1979', max_gradient: 9, weight: 1400, height: 1.95, description: 'Der beliebte Hippie-Bus' },
  
  // Italian Classics
  { name: 'Fiat 500 (Cinquecento)', country: '🇮🇹', year: '1957-1975', max_gradient: 10, weight: 470, description: 'Italienisches Kultauto' },
  { name: 'Alfa Romeo Spider', country: '🇮🇹', year: '1966-1994', max_gradient: 16, weight: 1050, description: 'Eleganter Roadster' },
  { name: 'Alfa Romeo Giulia', country: '🇮🇹', year: '1962-1978', max_gradient: 17, weight: 1020, description: 'Sportliche Limousine' },
  { name: 'Lancia Fulvia', country: '🇮🇹', year: '1963-1976', max_gradient: 14, weight: 950, description: 'Rallye-Legende' },
  { name: 'Ferrari 250 GTO', country: '🇮🇹', year: '1962-1964', max_gradient: 20, weight: 880, description: 'Wertvollster Oldtimer' },
  
  // British Classics
  { name: 'Mini Cooper (Classic)', country: '🇬🇧', year: '1961-2000', max_gradient: 14, weight: 620, description: 'Britische Ikone' },
  { name: 'MG MGB', country: '🇬🇧', year: '1962-1980', max_gradient: 14, weight: 920, description: 'Beliebter Roadster' },
  { name: 'Jaguar E-Type', country: '🇬🇧', year: '1961-1975', max_gradient: 18, weight: 1220, description: 'Schönster Sportwagen' },
  { name: 'Jaguar XK120/140/150', country: '🇬🇧', year: '1948-1961', max_gradient: 16, weight: 1300, description: 'Elegante Sportler' },
  { name: 'Triumph TR6', country: '🇬🇧', year: '1968-1976', max_gradient: 15, weight: 1070, description: 'Klassischer Roadster' },
  { name: 'Austin-Healey 3000', country: '🇬🇧', year: '1959-1967', max_gradient: 15, weight: 1130, description: 'Big Healey' },
  { name: 'Land Rover Series', country: '🇬🇧', year: '1948-1985', max_gradient: 20, weight: 1500, description: 'Geländewagen-Legende' },
  
  // French Classics
  { name: 'Citroën 2CV (Ente)', country: '🇫🇷', year: '1948-1990', max_gradient: 8, weight: 560, description: 'Die sympathische Ente' },
  { name: 'Citroën DS', country: '🇫🇷', year: '1955-1975', max_gradient: 14, weight: 1250, description: 'Die Göttin' },
  { name: 'Renault 4CV', country: '🇫🇷', year: '1947-1961', max_gradient: 10, weight: 560, description: 'Crèmeschnittchen' },
  { name: 'Peugeot 404', country: '🇫🇷', year: '1960-1975', max_gradient: 14, weight: 1080, description: 'Robuster Klassiker' },
  
  // American Classics
  { name: 'Ford Mustang (1. Gen)', country: '🇺🇸', year: '1964-1973', max_gradient: 16, weight: 1300, description: 'Pony Car Original' },
  { name: 'Chevrolet Corvette C1', country: '🇺🇸', year: '1953-1962', max_gradient: 17, weight: 1290, description: 'Amerikas Sportwagen' },
  { name: 'Chevrolet Corvette C2', country: '🇺🇸', year: '1963-1967', max_gradient: 18, weight: 1360, description: 'Stingray' },
  { name: 'Chevrolet Bel Air', country: '🇺🇸', year: '1950-1981', max_gradient: 14, weight: 1550, description: 'Amerikanischer Traum' },
  { name: 'Ford Thunderbird', country: '🇺🇸', year: '1955-1997', max_gradient: 15, weight: 1500, description: 'Personal Luxury Car' },
  { name: 'Cadillac Eldorado', country: '🇺🇸', year: '1953-2002', max_gradient: 12, weight: 2100, description: 'Luxus-Ikone' },
  { name: 'Dodge Charger (1. Gen)', country: '🇺🇸', year: '1966-1978', max_gradient: 16, weight: 1600, description: 'Muscle Car Legende' },
  
  // Swedish Classics
  { name: 'Volvo P1800', country: '🇸🇪', year: '1961-1973', max_gradient: 14, weight: 1080, description: 'Der Heilige' },
  { name: 'Volvo Amazon', country: '🇸🇪', year: '1956-1970', max_gradient: 14, weight: 1100, description: 'Robuster Schwede' },
  { name: 'Saab 96', country: '🇸🇪', year: '1960-1980', max_gradient: 12, weight: 850, description: 'Rallye-Sieger' },
  
  // Japanese Classics
  { name: 'Toyota 2000GT', country: '🇯🇵', year: '1967-1970', max_gradient: 18, weight: 1120, description: 'Japans erster Supersportwagen' },
  { name: 'Datsun 240Z', country: '🇯🇵', year: '1969-1978', max_gradient: 17, weight: 1040, description: 'Fairlady Z' },
  { name: 'Mazda MX-5 NA (Miata)', country: '🇯🇵', year: '1989-1998', max_gradient: 18, weight: 960, description: 'Roadster-Wiederbelebung' },
  { name: 'Honda S800', country: '🇯🇵', year: '1966-1970', max_gradient: 16, weight: 720, description: 'Kleiner Sportwagen' },
];

// Group cars by country for the selector
const carsByCountry = classicCars.reduce((acc, car) => {
  const country = car.country;
  if (!acc[country]) acc[country] = [];
  acc[country].push(car);
  return acc;
}, {});

const VehicleProfiles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [showClassicCarPicker, setShowClassicCarPicker] = useState(false);
  const [classicCarSearch, setClassicCarSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    vehicle_type: 'oldtimer',
    height: '',
    width: '',
    length: '',
    weight: '',
    max_gradient: 15,
    avoid_narrow_roads: true
  });

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`);
      setVehicles(response.data);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      toast.error('Fehler beim Laden der Fahrzeuge');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      vehicle_type: 'oldtimer',
      height: '',
      width: '',
      length: '',
      weight: '',
      max_gradient: 15,
      avoid_narrow_roads: true
    });
    setEditingVehicle(null);
  };

  const openDialog = (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setFormData({
        name: vehicle.name,
        vehicle_type: vehicle.vehicle_type,
        height: vehicle.height?.toString() || '',
        width: vehicle.width?.toString() || '',
        length: vehicle.length?.toString() || '',
        weight: vehicle.weight?.toString() || '',
        max_gradient: vehicle.max_gradient || 15,
        avoid_narrow_roads: vehicle.avoid_narrow_roads ?? true
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const applyPreset = (type) => {
    const preset = presetProfiles[type];
    if (preset) {
      setFormData(prev => ({
        ...prev,
        vehicle_type: type,
        max_gradient: preset.max_gradient,
        height: preset.height?.toString() || '',
        width: preset.width?.toString() || '',
        length: preset.length?.toString() || '',
        weight: preset.weight?.toString() || '',
        avoid_narrow_roads: preset.avoid_narrow_roads
      }));
    }
  };

  // Apply classic car preset
  const applyClassicCar = (car) => {
    setFormData(prev => ({
      ...prev,
      name: car.name,
      vehicle_type: 'oldtimer',
      max_gradient: car.max_gradient,
      height: car.height?.toString() || '',
      weight: car.weight?.toString() || '',
      avoid_narrow_roads: true
    }));
    toast.success(`${car.name} ausgewählt`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      name: formData.name,
      vehicle_type: formData.vehicle_type,
      height: formData.height ? parseFloat(formData.height) : null,
      width: formData.width ? parseFloat(formData.width) : null,
      length: formData.length ? parseFloat(formData.length) : null,
      weight: formData.weight ? parseFloat(formData.weight) : null,
      max_gradient: formData.max_gradient,
      avoid_narrow_roads: formData.avoid_narrow_roads
    };

    try {
      if (editingVehicle) {
        const response = await axios.put(`${API}/vehicles/${editingVehicle.id}`, data);
        setVehicles(vehicles.map(v => v.id === editingVehicle.id ? response.data : v));
        toast.success('Fahrzeug aktualisiert');
      } else {
        const response = await axios.post(`${API}/vehicles`, data);
        setVehicles([...vehicles, response.data]);
        toast.success('Fahrzeug hinzugefügt');
      }
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save vehicle:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const deleteVehicle = async (id) => {
    try {
      await axios.delete(`${API}/vehicles/${id}`);
      setVehicles(vehicles.filter(v => v.id !== id));
      toast.success('Fahrzeug gelöscht');
    } catch (error) {
      console.error('Failed to delete vehicle:', error);
      toast.error('Fehler beim Löschen');
    }
    setDeleteId(null);
  };

  const getVehicleIcon = (type) => {
    const found = vehicleTypes.find(v => v.value === type);
    return found ? found.icon : Car;
  };

  const getGradientLabel = (gradient) => {
    if (gradient <= 8) return { text: 'Flach', color: 'text-green-500' };
    if (gradient <= 12) return { text: 'Moderat', color: 'text-amber-500' };
    if (gradient <= 15) return { text: 'Steil', color: 'text-orange-500' };
    return { text: 'Sehr steil', color: 'text-red-500' };
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-full bg-background overflow-auto">
      <div className="p-4 sm:p-6 md:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2 sm:gap-3">
              <Car className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              Fahrzeuge
            </h1>
            <p className="text-sm text-muted-foreground mt-1 sm:mt-2">
              Konfiguriere deine Fahrzeuge für optimierte Routenplanung
            </p>
          </div>
          <Button 
            onClick={() => openDialog()}
            className="bg-primary hover:bg-primary/90 text-primary-foreground w-full sm:w-auto"
            data-testid="add-vehicle-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Fahrzeug hinzufügen
          </Button>
        </div>

        {/* Info Card */}
        <Card className="bg-primary/5 border-primary/20 mb-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Settings2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-foreground font-medium">Warum Fahrzeugprofile?</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Mit Fahrzeugprofilen erhältst du maßgeschneiderte Routenwarnungen. 
                  Ein Oldtimer mit schwachem Motor wird bei anderen Steigungen gewarnt als ein modernes Wohnmobil.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {vehicles.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-12 sm:py-16 text-center">
              <Car className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-foreground mb-2">Noch keine Fahrzeuge</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Füge dein erstes Fahrzeug hinzu, um optimierte Routenwarnungen zu erhalten.
              </p>
              <Button 
                onClick={() => openDialog()}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <Plus className="w-4 h-4 mr-2" />
                Erstes Fahrzeug anlegen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <ScrollArea className="h-[calc(100vh-280px)] sm:h-[calc(100vh-300px)]">
            <div className="grid gap-4 pr-2 sm:pr-4">
              {vehicles.map((vehicle) => {
                const VehicleIcon = getVehicleIcon(vehicle.vehicle_type);
                const gradientInfo = getGradientLabel(vehicle.max_gradient);
                return (
                  <Card key={vehicle.id} className="bg-card border-border hover:border-primary/30 transition-colors">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        {/* Icon & Name */}
                        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <VehicleIcon className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-display text-lg sm:text-xl text-foreground truncate">{vehicle.name}</h3>
                            <p className="text-xs sm:text-sm text-muted-foreground capitalize">
                              {vehicleTypes.find(t => t.value === vehicle.vehicle_type)?.label || vehicle.vehicle_type}
                            </p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 self-start">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-3"
                            onClick={() => openDialog(vehicle)}
                            data-testid={`edit-vehicle-${vehicle.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5 sm:mr-1" />
                            <span className="hidden sm:inline">Bearbeiten</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2 text-muted-foreground hover:text-destructive hover:border-destructive"
                            onClick={() => setDeleteId(vehicle.id)}
                            data-testid={`delete-vehicle-${vehicle.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-4 pt-4 border-t border-border">
                        {/* Max Gradient */}
                        <div className="bg-muted/30 rounded-lg p-3">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Mountain className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-[10px] sm:text-xs text-muted-foreground">Max. Steigung</span>
                          </div>
                          <p className={`text-lg sm:text-xl font-display font-bold ${gradientInfo.color}`}>
                            {vehicle.max_gradient}%
                          </p>
                          <p className={`text-[10px] ${gradientInfo.color}`}>{gradientInfo.text}</p>
                        </div>

                        {/* Dimensions */}
                        {(vehicle.height || vehicle.width || vehicle.length) && (
                          <div className="bg-muted/30 rounded-lg p-3">
                            <div className="flex items-center gap-1.5 mb-1">
                              <Ruler className="w-3.5 h-3.5 text-muted-foreground" />
                              <span className="text-[10px] sm:text-xs text-muted-foreground">Abmessungen</span>
                            </div>
                            <p className="text-sm sm:text-base font-medium text-foreground">
                              {vehicle.height && `H: ${vehicle.height}m`}
                              {vehicle.width && ` B: ${vehicle.width}m`}
                            </p>
                            {vehicle.length && <p className="text-xs text-muted-foreground">L: {vehicle.length}m</p>}
                          </div>
                        )}

                        {/* Weight */}
                        {vehicle.weight && (
                          <div className="bg-muted/30 rounded-lg p-3">
                            <div className="flex items-center gap-1.5 mb-1">
                              <Weight className="w-3.5 h-3.5 text-muted-foreground" />
                              <span className="text-[10px] sm:text-xs text-muted-foreground">Gewicht</span>
                            </div>
                            <p className="text-lg sm:text-xl font-display font-bold text-foreground">
                              {(vehicle.weight / 1000).toFixed(1)}t
                            </p>
                          </div>
                        )}

                        {/* Narrow Roads */}
                        <div className="bg-muted/30 rounded-lg p-3">
                          <div className="flex items-center gap-1.5 mb-1">
                            <AlertTriangle className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-[10px] sm:text-xs text-muted-foreground">Enge Straßen</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {vehicle.avoid_narrow_roads ? (
                              <>
                                <Check className="w-4 h-4 text-green-500" />
                                <span className="text-sm text-green-500">Vermeiden</span>
                              </>
                            ) : (
                              <>
                                <X className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">Erlaubt</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Add/Edit Vehicle Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-foreground font-display flex items-center gap-2">
              <Car className="w-5 h-5 text-primary" />
              {editingVehicle ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug'}
            </DialogTitle>
            <DialogDescription>
              {editingVehicle 
                ? 'Aktualisiere die Einstellungen deines Fahrzeugs.' 
                : 'Konfiguriere ein neues Fahrzeugprofil für optimierte Routen.'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-foreground">Fahrzeugname</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Mein VW Käfer"
                required
                className="bg-muted/50 border-border"
                data-testid="vehicle-name-input"
              />
            </div>

            {/* Vehicle Type */}
            <div className="space-y-2">
              <Label className="text-foreground">Fahrzeugtyp</Label>
              <div className="grid grid-cols-3 gap-2">
                {vehicleTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => applyPreset(type.value)}
                    className={`p-3 rounded-lg border transition-all text-center ${
                      formData.vehicle_type === type.value 
                        ? 'bg-primary/10 border-primary' 
                        : 'bg-muted/30 border-border hover:border-primary/50'
                    }`}
                  >
                    <type.icon className={`w-6 h-6 mx-auto mb-1 ${formData.vehicle_type === type.value ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className={`text-xs font-medium ${formData.vehicle_type === type.value ? 'text-primary' : 'text-foreground'}`}>
                      {type.label.split(' ')[0]}
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {vehicleTypes.find(t => t.value === formData.vehicle_type)?.description}
              </p>
            </div>

            {/* Classic Car Picker - Only show for Oldtimer type */}
            {formData.vehicle_type === 'oldtimer' && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setShowClassicCarPicker(!showClassicCarPicker)}
                  className="w-full flex items-center justify-between p-3 rounded-lg border border-dashed border-primary/50 bg-primary/5 hover:bg-primary/10 transition-colors"
                  data-testid="classic-car-picker-toggle"
                >
                  <div className="flex items-center gap-2">
                    <List className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium text-primary">Aus Oldtimer-Liste wählen</span>
                  </div>
                  {showClassicCarPicker ? <ChevronUp className="w-4 h-4 text-primary" /> : <ChevronDown className="w-4 h-4 text-primary" />}
                </button>
                
                {showClassicCarPicker && (
                  <div className="border border-border rounded-lg overflow-hidden">
                    {/* Search */}
                    <div className="p-2 border-b border-border">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          value={classicCarSearch}
                          onChange={(e) => setClassicCarSearch(e.target.value)}
                          placeholder="Suche nach Marke oder Modell..."
                          className="pl-8 h-8 text-sm bg-muted/50 border-0"
                          data-testid="classic-car-search"
                        />
                      </div>
                    </div>
                    
                    {/* Car List */}
                    <ScrollArea className="h-48">
                      <div className="p-1">
                        {Object.entries(carsByCountry).map(([country, cars]) => {
                          const filteredCars = cars.filter(car => 
                            car.name.toLowerCase().includes(classicCarSearch.toLowerCase()) ||
                            car.description.toLowerCase().includes(classicCarSearch.toLowerCase())
                          );
                          if (filteredCars.length === 0) return null;
                          
                          return (
                            <div key={country} className="mb-2">
                              <div className="px-2 py-1 text-xs font-medium text-muted-foreground sticky top-0 bg-card">
                                {country} {country === '🇩🇪' ? 'Deutschland' : country === '🇮🇹' ? 'Italien' : country === '🇬🇧' ? 'Großbritannien' : country === '🇫🇷' ? 'Frankreich' : country === '🇺🇸' ? 'USA' : country === '🇸🇪' ? 'Schweden' : country === '🇯🇵' ? 'Japan' : ''}
                              </div>
                              {filteredCars.map((car, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => {
                                    applyClassicCar(car);
                                    setShowClassicCarPicker(false);
                                    setClassicCarSearch('');
                                  }}
                                  className="w-full text-left px-2 py-2 rounded hover:bg-muted/50 transition-colors"
                                  data-testid={`classic-car-${idx}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-foreground">{car.name}</span>
                                    <span className="text-xs text-muted-foreground">{car.year}</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-xs text-muted-foreground">{car.description}</span>
                                    <span className="text-xs text-primary">max. {car.max_gradient}%</span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>
            )}

            {/* Max Gradient Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-foreground flex items-center gap-2">
                  <Mountain className="w-4 h-4" />
                  Max. Steigung
                </Label>
                <span className={`text-lg font-display font-bold ${getGradientLabel(formData.max_gradient).color}`}>
                  {formData.max_gradient}%
                </span>
              </div>
              <Slider
                value={[formData.max_gradient]}
                onValueChange={([value]) => setFormData({ ...formData, max_gradient: value })}
                min={5}
                max={25}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>5% (sehr flach)</span>
                <span>25% (sehr steil)</span>
              </div>
              <p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded">
                Bei Steigungen über {formData.max_gradient}% wirst du gewarnt. 
                {formData.max_gradient <= 10 && ' Ideal für Oldtimer mit schwacher Motorleistung.'}
                {formData.max_gradient > 10 && formData.max_gradient <= 15 && ' Geeignet für die meisten Fahrzeuge.'}
                {formData.max_gradient > 15 && ' Nur für leistungsstarke Fahrzeuge empfohlen.'}
              </p>
            </div>

            {/* Dimensions */}
            <div className="space-y-2">
              <Label className="text-foreground flex items-center gap-2">
                <Ruler className="w-4 h-4" />
                Abmessungen (optional)
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="height" className="text-xs text-muted-foreground">Höhe (m)</Label>
                  <Input
                    id="height"
                    type="number"
                    step="0.1"
                    value={formData.height}
                    onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                    placeholder="z.B. 2.5"
                    className="bg-muted/50 border-border"
                    data-testid="vehicle-height-input"
                  />
                </div>
                <div>
                  <Label htmlFor="width" className="text-xs text-muted-foreground">Breite (m)</Label>
                  <Input
                    id="width"
                    type="number"
                    step="0.1"
                    value={formData.width}
                    onChange={(e) => setFormData({ ...formData, width: e.target.value })}
                    placeholder="z.B. 2.2"
                    className="bg-muted/50 border-border"
                    data-testid="vehicle-width-input"
                  />
                </div>
                <div>
                  <Label htmlFor="length" className="text-xs text-muted-foreground">Länge (m)</Label>
                  <Input
                    id="length"
                    type="number"
                    step="0.1"
                    value={formData.length}
                    onChange={(e) => setFormData({ ...formData, length: e.target.value })}
                    placeholder="z.B. 5.0"
                    className="bg-muted/50 border-border"
                    data-testid="vehicle-length-input"
                  />
                </div>
                <div>
                  <Label htmlFor="weight" className="text-xs text-muted-foreground">Gewicht (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    placeholder="z.B. 3500"
                    className="bg-muted/50 border-border"
                    data-testid="vehicle-weight-input"
                  />
                </div>
              </div>
            </div>

            {/* Avoid Narrow Roads */}
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <Label htmlFor="avoidNarrow" className="text-foreground flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Enge Straßen vermeiden
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Warnt bei schmalen Straßen
                </p>
              </div>
              <Switch
                id="avoidNarrow"
                checked={formData.avoid_narrow_roads}
                onCheckedChange={(checked) => setFormData({ ...formData, avoid_narrow_roads: checked })}
                data-testid="vehicle-narrow-switch"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1 border-border"
                onClick={() => setDialogOpen(false)}
              >
                Abbrechen
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="save-vehicle-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingVehicle ? 'Aktualisieren' : 'Speichern'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-border mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Fahrzeug löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Das Fahrzeugprofil wird dauerhaft gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-muted text-foreground border-border">Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deleteVehicle(deleteId)}
              className="bg-destructive text-destructive-foreground"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VehicleProfiles;
